import { useState } from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  CssBaseline,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/SpaceDashboard';
import EventIcon from '@mui/icons-material/Event';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PeopleIcon from '@mui/icons-material/Groups2';
import SchoolIcon from '@mui/icons-material/School';
import FamilyIcon from '@mui/icons-material/FamilyRestroom';
import StadiumIcon from '@mui/icons-material/Stadium';
import LibraryIcon from '@mui/icons-material/CollectionsBookmark';
import EmailIcon from '@mui/icons-material/Mail';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ChildIcon from '@mui/icons-material/EmojiPeople';
import ShieldIcon from '@mui/icons-material/AdminPanelSettings';

import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { api, apiErrorMessage } from '../api/client';
import type { Org, UserRole } from '../api/types';

const DRAWER_WIDTH = 252;

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  roles: UserRole[];
}

const NAV: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: <DashboardIcon />, roles: ['admin', 'coach', 'parent', 'student'] },
  { label: 'Setup', to: '/setup', icon: <RocketLaunchIcon />, roles: ['admin', 'coach'] },
  { label: 'Meets', to: '/meets', icon: <EventIcon />, roles: ['admin', 'coach'] },
  { label: 'Students', to: '/students', icon: <SchoolIcon />, roles: ['admin', 'coach'] },
  { label: 'Coaches', to: '/coaches', icon: <PeopleIcon />, roles: ['admin'] },
  { label: 'Parents', to: '/parents', icon: <FamilyIcon />, roles: ['admin', 'coach'] },
  { label: 'Venues', to: '/venues', icon: <StadiumIcon />, roles: ['admin', 'coach'] },
  { label: 'Event library', to: '/event-library', icon: <LibraryIcon />, roles: ['admin', 'coach'] },
  { label: 'Parent emails', to: '/notifications', icon: <EmailIcon />, roles: ['admin', 'coach'] },
  { label: 'My Children', to: '/my-children', icon: <ChildIcon />, roles: ['parent'] },
  { label: 'Settings', to: '/settings', icon: <SettingsIcon />, roles: ['admin'] },
];

export default function AppLayout() {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactError, setContactError] = useState<string | null>(null);
  const [contactSent, setContactSent] = useState(false);
  const [contactBusy, setContactBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const orgQuery = useQuery<Org>({
    queryKey: ['org', 'me'],
    enabled: !!user,
    queryFn: async () => (await api.get('/orgs/me')).data,
  });

  if (!user) return null;

  const visibleNav = NAV.filter(
    (item) => item.roles.includes(user.role) || (user.role === 'super_admin' && item.roles.includes('admin')),
  );

  const handleLogout = () => {
    setMenuAnchor(null);
    logout();
    navigate('/login', { replace: true });
  };

  const openContact = () => {
    setContactSubject('');
    setContactMessage('');
    setContactError(null);
    setContactSent(false);
    setContactOpen(true);
  };

  const submitContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactMessage.trim()) {
      setContactError('Please describe what happened.');
      return;
    }
    setContactBusy(true);
    setContactError(null);
    try {
      const context = [
        '',
        '---',
        `Signed-in user: ${user.full_name || user.email} <${user.email}>`,
        `Role: ${user.role}`,
        `Org: ${orgQuery.data?.name || 'Unknown org'}`,
        `Current page: ${window.location.href}`,
      ].join('\n');
      await api.post('/contact', {
        name: user.full_name || user.email,
        email: user.email,
        subject: contactSubject.trim() || 'Bug report / support request',
        message: `${contactMessage.trim()}${context}`,
      });
      setContactSent(true);
      setContactSubject('');
      setContactMessage('');
    } catch (err) {
      setContactError(apiErrorMessage(err, 'Could not send your message. Please try again.'));
    } finally {
      setContactBusy(false);
    }
  };

  const drawerContent = (
    <Box
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}
      role="navigation"
      aria-label="Main navigation"
    >
      <Box sx={{ px: 2.5, py: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            sx={{
              position: 'relative',
              width: 36,
              height: 36,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #0b5394 0%, #1e88e5 100%)',
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 800,
              fontSize: 18,
              letterSpacing: '-1px',
            }}
            aria-hidden
          >
            B
            <Box
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 3,
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'secondary.main',
              }}
            />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {orgQuery.data?.name || 'Baton'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Track &amp; field workspace
            </Typography>
          </Box>
        </Stack>
      </Box>
      <Divider />
      <List sx={{ px: 1.5, py: 1.5, flex: 1 }}>
        {visibleNav.map((item) => {
          const selected =
            location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
          return (
            <ListItem key={item.to} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={RouterLink}
                to={item.to}
                selected={selected}
                onClick={() => setMobileOpen(false)}
                aria-label={item.label}
                sx={{
                  borderRadius: 1.5,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                    '&:hover': { bgcolor: 'primary.dark' },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: selected ? 'inherit' : 'text.secondary' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Stack spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EmailIcon />}
            onClick={openContact}
            aria-label="Contact us or report a bug"
            fullWidth
          >
            Contact us
          </Button>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            v0.1.0
          </Typography>
        </Stack>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="Open navigation"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 1, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {user.role === 'parent' ? 'Parent portal' : user.role === 'student' ? 'Athlete portal' : 'Workspace'}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {orgQuery.data?.name || 'Baton'}
            </Typography>
          </Box>

          <Tooltip title={`${user.full_name} (${user.email})`}>
            <IconButton
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              aria-label="Open user menu"
              size="large"
            >
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                {(user.first_name?.[0] || user.email[0]).toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ px: 2, py: 1.25 }}>
              <Typography variant="subtitle2">{user.full_name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              component={RouterLink}
              to="/profile"
              onClick={() => setMenuAnchor(null)}
              aria-label="Profile and password"
            >
              <ListItemIcon><AccountIcon fontSize="small" /></ListItemIcon>
              Profile & password
            </MenuItem>
            {user.role === 'super_admin' && (
              <MenuItem
                component={RouterLink}
                to="/super-admin"
                onClick={() => setMenuAnchor(null)}
                aria-label="Open super admin console"
              >
                <ListItemIcon><ShieldIcon fontSize="small" color="secondary" /></ListItemIcon>
                Super admin
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout} aria-label="Log out">
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Log out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
        aria-label="Main"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          open={isLg}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
          <Outlet />
        </Box>
      </Box>

      <Dialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="contact-support-title"
      >
        <DialogTitle id="contact-support-title">Contact us</DialogTitle>
        <form onSubmit={submitContact} aria-label="Contact support form">
          <DialogContent dividers>
            <Stack spacing={2}>
              {contactSent && (
                <Alert severity="success">
                  Thanks, your message was sent. We'll reply to {user.email}.
                </Alert>
              )}
              {contactError && (
                <Alert severity="error" onClose={() => setContactError(null)}>
                  {contactError}
                </Alert>
              )}
              <Typography color="text.secondary">
                Report a bug, ask a question, or tell us where the workflow is confusing.
              </Typography>
              <TextField
                label="Subject"
                value={contactSubject}
                onChange={(e) => setContactSubject(e.target.value)}
                placeholder="Bug report"
                inputProps={{ 'aria-label': 'Contact subject', maxLength: 200 }}
              />
              <TextField
                label="What happened?"
                value={contactMessage}
                onChange={(e) => setContactMessage(e.target.value)}
                required
                multiline
                minRows={5}
                inputProps={{ 'aria-label': 'Contact message', maxLength: 4000 }}
              />
              <Typography variant="caption" color="text.secondary">
                We'll include your account email, role, organization, and current page so we can debug faster.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContactOpen(false)} aria-label="Close contact form">
              Close
            </Button>
            <Button type="submit" variant="contained" disabled={contactBusy} aria-label="Send support message">
              {contactBusy ? 'Sending…' : 'Send message'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

